import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, Plus, Trash2, Star, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useCompare } from "@/context/CompareContext";
import { compareApps, listApps } from "@/lib/api";
import { formatPlanPrice, planModel, planEffectiveMonthly } from "@/lib/pricing";
import { COMPARE } from "@/constants/testIds";
import AdSlot from "@/components/AdSlot";
import AppLogo from "@/components/AppLogo";
import OgPreviewTooltip from "@/components/OgPreviewTooltip";

const ComparePage = () => {
  const { t, lang } = useLang();
  const { slugs, remove, clear, add } = useCompare();
  const [data, setData] = useState({ apps: [], feature_categories: {} });
  const [pool, setPool] = useState([]);

  useEffect(() => {
    listApps({ sort: "score", limit: 100 }).then(setPool).catch(() => setPool([]));
  }, []);

  useEffect(() => {
    if (slugs.length === 0) {
      setData({ apps: [], feature_categories: {} });
      return;
    }
    compareApps(slugs).then(setData).catch(() => setData({ apps: [], feature_categories: {} }));
  }, [slugs]);

  const apps = data.apps || [];
  const cats = data.feature_categories || {};

  return (
    <main className="hac-container py-12 md:py-16">
      <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline mb-3">{t.nav.compare}</div>
          <h1 className="font-heading text-4xl md:text-5xl text-[#0F172A] leading-tight">
            {t.nav.compare}
          </h1>
          <p className="text-[#64748B] mt-2">
            {slugs.length} {t.common.ofApps} · {Object.keys(cats).length}{" "}
            {lang === "nl" ? "categorieën" : "categories"}
          </p>
        </div>
        {slugs.length > 0 && (
          <button
            onClick={clear}
            data-testid={COMPARE.clear}
            className="hac-btn-secondary"
          >
            <Trash2 size={14} /> {t.common.clearAll}
          </button>
        )}
      </div>

      {apps.length < 2 && (
        <div className="bg-[#F1F5F9] border border-[#E2E8F0] p-6 mb-8">
          <p className="text-[#64748B] mb-4">{t.common.selectAtLeastTwo}</p>
          <div className="flex flex-wrap gap-2 max-h-60 overflow-y-auto">
            {pool
              .filter((p) => !slugs.includes(p.slug))
              .slice(0, 24)
              .map((p) => (
                <button
                  key={p.slug}
                  onClick={() => add(p.slug)}
                  className="hac-chip hover:bg-[#2563EB] hover:text-white transition-colors cursor-pointer"
                  data-testid={`compare-add-${p.slug}`}
                >
                  <Plus size={12} /> {p.name}
                </button>
              ))}
          </div>
        </div>
      )}

      {apps.length >= 1 && (
        <div className="scroll-x border border-[#E2E8F0] bg-white">
          <table data-testid={COMPARE.table} className="w-full min-w-max border-collapse text-sm">
            <thead>
              <tr>
                <th className="bg-[#F1F5F9] font-heading text-base text-[#0F172A] p-5 text-left border-b border-[#E2E8F0] sticky left-0 z-10 min-w-[200px]">
                  {t.common.featuresLabel}
                </th>
                {apps.map((a) => (
                  <th
                    key={a.slug}
                    className="bg-[#F1F5F9] p-5 border-b border-[#E2E8F0] border-l border-[#E2E8F0] min-w-[180px] align-top"
                  >
                    <div className="flex flex-col items-start gap-2">
                      <OgPreviewTooltip slug={a.slug} className="inline-block">
                        <div className="flex items-center gap-2">
                          <AppLogo app={a} className="w-8 h-8 rounded-md" textClassName="text-sm" />
                          <Link
                            to={`/apps/${a.slug}`}
                            className="font-heading text-xl text-[#0F172A] hover:text-[#0F172A]"
                          >
                            {a.name}
                          </Link>
                        </div>
                      </OgPreviewTooltip>
                      <button
                        onClick={() => remove(a.slug)}
                      className="text-xs text-[#94A3B8] hover:text-[#EF4444] inline-flex items-center gap-1"
                        data-testid={`compare-remove-${a.slug}`}
                      >
                        <X size={12} /> {t.common.remove}
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="p-4 border-b border-[#E2E8F0] sticky left-0 bg-white font-medium">
                  {t.common.pricing}
                </td>
                {apps.map((a) => (
                  <td
                    key={a.slug}
                    className="p-4 border-b border-l border-[#E2E8F0] text-[#64748B]"
                  >
                    {a.pricing?.free_tier && a.pricing?.monthly_usd === 0
                      ? t.common.free
                      : `$${a.pricing?.monthly_usd}${t.common.monthly}`}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="p-4 border-b border-[#E2E8F0] sticky left-0 bg-white font-medium">
                  {t.common.ratingLabel}
                </td>
                {apps.map((a) => (
                  <td
                    key={a.slug}
                    className="p-4 border-b border-l border-[#E2E8F0] font-mono"
                  >
                    {a.rating?.toFixed(1)} ★
                  </td>
                ))}
              </tr>
              <tr>
                <td className="p-4 border-b border-[#E2E8F0] sticky left-0 bg-white font-medium">
                  {t.common.platforms}
                </td>
                {apps.map((a) => (
                  <td
                    key={a.slug}
                    className="p-4 border-b border-l border-[#E2E8F0] text-[#64748B] text-xs"
                  >
                    {[a.platforms?.ios && "iOS", a.platforms?.android && "Android", a.platforms?.web && "Web"]
                      .filter(Boolean)
                      .join(" · ")}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="p-4 border-b border-[#E2E8F0] sticky left-0 bg-white font-medium">
                  {t.common.country}
                </td>
                {apps.map((a) => (
                  <td
                    key={a.slug}
                    className="p-4 border-b border-l border-[#E2E8F0] text-[#64748B]"
                  >
                    {a.country}
                  </td>
                ))}
              </tr>

              {Object.entries(cats).map(([cat, feats]) => (
                <React.Fragment key={cat}>
                  <tr>
                    <td
                      colSpan={apps.length + 1}
                      className="p-4 bg-[#F8FAFC] font-heading text-[#0F172A] text-lg border-b border-[#E2E8F0] sticky left-0"
                    >
                      {t.categories[cat]}
                    </td>
                  </tr>
                  {feats.map((f) => (
                    <tr key={f}>
                      <td className="p-3 border-b border-[#E2E8F0] sticky left-0 bg-white text-[#64748B]">
                        {t.features[f] || f}
                      </td>
                      {apps.map((a) => {
                        const has = (a.features || []).includes(f);
                        return (
                          <td
                            key={a.slug}
                            className="p-3 border-b border-l border-[#E2E8F0] text-center"
                          >
                            {has ? (
                              <Check size={16} className="text-[#0F172A] inline" />
                            ) : (
                              <X size={14} className="text-[#CBD5E1] inline" />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {apps.length > 0 && <AdSlot placement="compare-top" slugs={apps.map((a) => a.slug)} />}

      {apps.length >= 2 && apps.some((a) => Array.isArray(a.plans) && a.plans.length > 0) && (
        <section className="mt-16" data-testid="compare-plans-section">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
            <div>
              <div className="overline mb-2">{lang === "nl" ? "Prijzen & modules" : "Pricing & modules"}</div>
              <h2 className="font-heading text-2xl md:text-3xl text-[#0F172A] leading-tight">
                {lang === "nl" ? "Welke plannen biedt elke app?" : "Which plans does each app offer?"}
              </h2>
            </div>
            <p className="text-xs text-[#64748B] max-w-md">
              {lang === "nl"
                ? "Side-by-side prijsvergelijking — beheerd door de app-eigenaren zelf."
                : "Side-by-side pricing comparison — managed by each app owner."}
            </p>
          </div>

          <div className={`grid gap-4 ${apps.length === 2 ? "md:grid-cols-2" : apps.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2 lg:grid-cols-4"}`}>
            {apps.map((a) => {
              const plans = Array.isArray(a.plans) ? a.plans : [];
              const hasFree = plans.some((p) => planModel(p) === "free");
              const paidPlans = plans
                .map((p) => ({ p, eff: planEffectiveMonthly(p) }))
                .filter((x) => x.eff > 0)
                .sort((x, y) => x.eff - y.eff);
              const cheapestPaid = paidPlans[0]?.p;
              return (
                <div key={a.slug} className="bg-white border border-[#E2E8F0] rounded-2xl p-5" data-testid={`compare-plans-${a.slug}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <AppLogo app={a} className="w-10 h-10 rounded-lg" textClassName="text-base" />
                    <div className="font-heading text-lg text-[#0F172A]">{a.name}</div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {hasFree ? (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded-full" data-testid={`compare-free-tier-${a.slug}`}>
                        ✓ {lang === "nl" ? "Gratis versie" : "Free tier"}
                      </span>
                    ) : plans.length > 0 ? (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-[#64748B] bg-[#F1F5F9] border border-[#E2E8F0] px-2 py-0.5 rounded-full" data-testid={`compare-paid-only-${a.slug}`}>
                        {lang === "nl" ? "Geen gratis versie" : "Paid only"}
                      </span>
                    ) : null}
                    {cheapestPaid && (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-[#2563EB] bg-[#EFF6FF] border border-[#DBEAFE] px-2 py-0.5 rounded-full">
                        {lang === "nl" ? "Vanaf" : "From"} {formatPlanPrice(cheapestPaid, lang).primary}
                      </span>
                    )}
                  </div>

                  {plans.length === 0 ? (
                    <p className="text-xs text-[#94A3B8] italic py-4">
                      {lang === "nl" ? "Deze app-eigenaar heeft nog geen prijsplannen gedeeld." : "This app owner hasn't shared pricing tiers yet."}
                    </p>
                  ) : (
                    <ul className="space-y-3">
                      {plans.map((p, i) => {
                        const { primary } = formatPlanPrice(p, lang);
                        return (
                          <li key={`${p.name}-${i}`} className={`border rounded-lg p-3 ${p.popular ? "border-amber-400 bg-amber-50/40" : "border-[#E2E8F0]"}`} data-testid={`compare-plan-${a.slug}-${i}`}>
                            <div className="flex items-baseline justify-between gap-2 mb-1">
                              <div className="font-bold text-[#0F172A] inline-flex items-center gap-1.5">
                                {p.popular && <Star size={11} className="fill-amber-500 text-amber-500" />}
                                {p.name}
                              </div>
                              <div className="font-mono text-xs text-[#0F172A] whitespace-nowrap">
                                {primary}
                              </div>
                            </div>
                            {p.description && <p className="text-[11px] text-[#64748B] mb-2 leading-snug">{p.description}</p>}
                            {(p.features || []).length > 0 && (
                              <ul className="space-y-0.5 text-[11px] text-[#0F172A]">
                                {p.features.slice(0, 5).map((f, fi) => (
                                  <li key={fi} className="flex items-start gap-1.5">
                                    <Check size={10} className="text-emerald-600 mt-1 shrink-0" />
                                    <span>{f}</span>
                                  </li>
                                ))}
                                {p.features.length > 5 && (
                                  <li className="text-[10px] text-[#94A3B8] italic pl-4">+ {p.features.length - 5} {lang === "nl" ? "meer" : "more"}</li>
                                )}
                              </ul>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  <Link
                    to={`/apps/${a.slug}#plans`}
                    className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-[#2563EB] hover:text-[#1D4ED8] hover:gap-1.5 transition-all"
                    data-testid={`compare-plans-review-${a.slug}`}
                  >
                    {lang === "nl" ? `Bekijk volledige ${a.name} review` : `View full ${a.name} review`} <ArrowRight size={11} />
                  </Link>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {apps.length > 0 && <AdSlot placement="compare-bottom" slugs={apps.map((a) => a.slug)} />}
    </main>
  );
};

export default ComparePage;
